<template>
  <action-dispatcher
    action="product/getProduct"
    ref="getProductDispatcher"
    :parameters="$route.params.id"
    @success="afterLoad"
  >
    <bp-header
      class="mb-7"
      title="Configurar checkout"
      subtitle="Siga as etapas a seguir para configurar e personalizar seu checkout."
    />

    <div class="row py-6">
      <aside class="col-lg-3 view-content-aside">
        <steps
          @change-step="step => (currentStep = step)"
          :step="currentStep"
          class="my-lg-0 my-6"
          :names="[
            'Dados da venda',
            'Opções de pagamento',
            'Formulário de compra',
            'Modelo de checkout',
            'Opções de suporte',
            'Página de Obrigado'
          ]"
        />
      </aside>

      <div class="col-lg-9 view-content-main">
        <step-title
          class="p-5 mb-5"
          :title="stepTitle.title"
          :description="stepTitle.description"
          :icon="stepTitle.icon"
          fa
        />
        <selling-data
          v-if="currentStep === 1"
          :form="form"
          :errors="errors"
          @next="nextStep"
          @cancel="backStep"
        />
        <payment-options
          v-if="currentStep === 2 && !notificationVisible"
          @next="nextStep"
          @cancel="backStep"
          @create="createNotification"
          @edit="editNotification"
          :form="form"
          :errors="errors"
          :notificationTypes="notificationTypes"
        />
        <form-buy
          v-if="currentStep === 3"
          @save="savedOptionForm"
          @cancel="backStep"
          :optionForm="form.option_form"
        />

        <checkout-layout
          v-if="currentStep === 4"
          :product="product"
          @next="nextStep"
          @cancel="backStep"
        />

        <support-options
          v-if="currentStep === 5"
          @next="nextStep"
          @cancel="backStep"
          @save="saveSupport"
          @remove="removeSupport"
          :product-id="$route.params.id"
          :form="form"
          :errors="errors"
        />
        <thanks-page
          v-if="currentStep === 6"
          @next="onSubmit"
          @cancel="backStep"
          :loading="loading"
          :form="form"
          :errors="errors"
        />

        <notification
          v-if="currentStep === 2 && notificationVisible"
          :product-id="$route.params.id"
          :notifications="form.show_notifications"
          :notificationToEdit="notificationToEdit"
          :notificationTypes="notificationTypes"
          @cancel="notificationVisible = false"
          @saveNewNotification="addNewNotification"
          @saveEditNotification="saveEditNotification"
          @remove="removeNotification"
        />
      </div>
    </div>
  </action-dispatcher>
</template>

<script>
import CheckoutLayout from "@/components/product/checkout/CheckoutLayout.vue";
import FormBuy from "@/components/product/checkout/FormBuy";
import Notification from "@/components/product/checkout/Notification";
import PaymentOptions from "@/components/product/checkout/PaymentOptions";
import SellingData from "@/components/product/checkout/SellingData";
import SupportOptions from "@/components/product/checkout/SupportOptions";
import ThanksPage from "@/components/product/checkout/ThanksPage";
import { formFields } from "@/functions/helpers.js";
import EditProductMixin from "@/mixins/editProduct.js";
import { mapState } from "vuex";

export default {
  mixins: [EditProductMixin],
  components: {
    SellingData,
    PaymentOptions,
    FormBuy,
    CheckoutLayout,
    SupportOptions,
    Notification,
    ThanksPage
  },
  data() {
    return {
      ...formFields(
        [
          "status",
          "email_support",
          "statement_descriptor",
          "expire_billet",
          "order_installment",
          "is_tax_to_producer",
          "payment_methods_ids",
          "allow_week_billet",
          "show_notifications",
          "option_form",
          "option_support",
          "url_redirect",
          "url_redirect_waiting",
          "url_redirect_credit_analysis",
          "has_redirect_pix"
        ],
        {
          payment_methods_ids: []
        }
      ),
      currentStep: 1,
      notificationVisible: false,
      notificationToEdit: null,
      notificationTypes: [
        {
          id: "SHOPPER_INTEREST_24_HOUR",
          name: "Pessoas interessantes nas últimas 24 horas"
        },
        {
          id: "SHOPPER_INTEREST_1_WEEK",
          name: "Pessoas interessadas na última semana"
        },
        {
          id: "SHOPPER_IN_CHECKOUT_10_MIN",
          name: "Pessoas interessadas nos últimos 10 minutos"
        },
        {
          id: "SALES_PROCESSED_24_HOUR",
          name: "Compras realizadas nas últimas 24 horas"
        },
        {
          id: "SALES_PROCESSED_1_WEEK",
          name: "Compras realizadas na última semana"
        }
      ]
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    breadcrumbs() {
      return [
        { link: "product", title: "Meus produtos" },
        {
          link: "product-details",
          title: this.product ? this.product["name"] : "...",
          params: { id: this.$route.params.id }
        },
        { title: "Configurar Checkout" }
      ];
    },
    stepTitle() {
      return {
        1: {
          title: "Dados de venda",
          description: "Configure as informações básicas da venda",
          icon: "fa-solid fa-info"
        },
        2: {
          title: "Opções de pagamento",
          description:
            "Configure as opções de pagamento que serão disponibilizadas",
          icon: "fa-solid fa-dollar-sign"
        },
        3: {
          title: "Formulário de compra",
          description:
            "Configure as informações que serão solicitadas ao cliente no formulário",
          icon: "fa-regular fa-file-lines"
        },
        4: {
          title: "Modelo de checkout",
          description:
            "Escolha entre um dos nossos templates. Você pode visualizar o template escolhido na versão desktop ou mobile.",
          icon: "fa-solid fa-fill-drip"
        },
        5: {
          title: "Opções de suporte",
          description:
            "Configure as opções de pagamento que serão disponibilizadas",
          icon: "fa-solid fa-headset"
        },
        6: {
          title: "Página de Obrigado",
          description:
            "Esta página aparecerá por último, após o cliente concluir a compra.",
          icon: "fa-solid fa-check"
        }
      }[this.currentStep];
    }
  },
  methods: {
    saveSupport(data) {
      this.form.option_support.push(data);
    },
    removeSupport() {
      this.form.option_support = [];
    },
    onSubmitError(error) {
      const stepFields = {
        1: ["status", "email_support", "statement_descriptor"],
        2: [
          "payment_methods_ids",
          "expire_billet",
          "allow_week_billet",
          "order_installment"
        ],
        3: ["option_form"],
        4: ["option_support"],
        5: [
          "url_redirect",
          "url_redirect_waiting",
          "url_redirect_credit_analysis"
        ]
      };
      for (let e in error) {
        for (let f in stepFields) {
          if (stepFields[f].includes(e) && this.currentStep > parseInt(f)) {
            this.currentStep = parseInt(f);
            break;
          }
        }
      }
    },
    afterLoad() {
      this.form = this.product;
      if (!this.form.whatsapp_support) {
        this.form.whatsapp_support = {
          status: "inactive",
          text: "",
          number: "",
          ddi: ""
        };
      }
      this.form.payment_methods_ids = this.product.payment_methods.map(
        p => p.id
      );
    },
    nextStep() {
      this.currentStep++;
    },
    backStep() {
      this.currentStep--;
      if (this.currentStep < 1) {
        this.$router.go(-1);
      }
    },
    savedOptionForm(data) {
      // if (this.form.option_form.id) {
      //   data.id = this.form.option_form.id
      // }
      this.form.option_form = data;
      this.currentStep++;
    },
    createNotification() {
      this.notificationVisible = true;
      this.notificationToEdit = null;
    },
    editNotification(index) {
      this.notificationVisible = true;
      this.notificationToEdit = index;
    },
    addNewNotification(val) {
      this.form.show_notifications.push(val);
      this.notificationVisible = false;
    },
    saveEditNotification(val) {
      this.form.show_notifications[this.notificationToEdit] = val;
      this.notificationVisible = false;
      this.notificationToEdit = null;
    },
    removeNotification() {
      this.form.show_notifications.splice(this.notificationToEdit, 1);
      this.notificationVisible = false;
    }
  }
};
</script>
